import {
  Alert,
  Button,
  Checkbox,
  Icons,
  Link,
  Modal,
  ModalClose,
  ModalDialog,
  Snackbar,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { usePrepareRedirectToRevokeHeurekaPortalScreen } from '../api/generated';
import { selectedOrganizationAtom } from '../state/selectedOrganization';
import { ModalTitle } from './ModalTitle';

interface DisconnectHeurekaDialogProps {
  open: boolean;
  onClose: () => void;
}

export function DisconnectHeurekaDialog({ open, onClose }: DisconnectHeurekaDialogProps) {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [acknowledged, setAcknowledged] = useState(noAcknowledgements);
  const [showDisconnectError, setShowDisconnectError] = useState(false);

  const disconnectHeurekaMutation = usePrepareRedirectToRevokeHeurekaPortalScreen({
    mutation: {
      onSuccess: (data: string) => {
        // Redirect the user to the Heureka portal to confirm they want to remove the connection
        window.location.href = data;
      },
      onError: () => {
        setShowDisconnectError(true);
      },
    },
  });

  const allAcknowledged = consequenceOptions.every(({ key }) => acknowledged[key]);

  const handleCheckboxChange = (key: ConsequenceKey, isSelected: boolean) => {
    setAcknowledged((previous) => ({ ...previous, [key]: isSelected }));
  };

  const handleClose = () => {
    onClose();
    setAcknowledged(noAcknowledgements);
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalDialog sx={{ maxWidth: 520 }}>
          <ModalClose />
          <ModalTitle>Verbindung trennen</ModalTitle>
          <Stack gap={2}>
            <Typography>
              Wenn Sie die Verbindung zu Heureka Health trennen, hat dies folgende Auswirkungen. Bitte bestätigen Sie
              jeden Punkt, um fortzufahren.
            </Typography>
            <Stack gap={2} paddingBlock={1.5}>
              {consequenceOptions.map(({ key, label }) => (
                <Checkbox
                  key={key}
                  checked={acknowledged[key]}
                  onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                  label={label}
                />
              ))}
            </Stack>
            <Alert startDecorator={<Icons.InfoOutlined />}>
              <Typography level={'inherit'}>
                Sind Sie sich nicht sicher? Kontaktieren Sie uns unter{' '}
                <Link href="mailto:support@healthinal.com">support@healthinal.com</Link>.
              </Typography>
            </Alert>
          </Stack>
          <Stack direction="row" gap={2} justifyContent="flex-end" mt={2}>
            <Button variant="plain" color="neutral" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button
              color="danger"
              disabled={!allAcknowledged}
              loading={disconnectHeurekaMutation.isPending}
              onClick={() => disconnectHeurekaMutation.mutate({ practiceId: selectedOrganization.id })}>
              Verbindung trennen
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Snackbar
        color="danger"
        open={showDisconnectError}
        onClose={() => setShowDisconnectError(false)}
        autoHideDuration={3000}
        startDecorator={<Icons.ErrorOutline />}>
        Beim Trennen der Verbindung ist ein Fehler aufgetreten.
      </Snackbar>
    </>
  );
}

type ConsequenceKey = 'manualLinks' | 'appAccess' | 'healthcareProviders';

type ConsequenceOption = {
  key: ConsequenceKey;
  label: string;
};

const consequenceOptions: ConsequenceOption[] = [
  {
    key: 'manualLinks',
    label:
      'Alle manuell erstellten Verknüpfungen von Patienten werden aufgelöst und müssen bei einer erneuten Verbindung neu erstellt werden.',
  },
  {
    key: 'appAccess',
    label: 'Die Patienten-App kann nicht mehr verwendet werden.',
  },
  {
    key: 'healthcareProviders',
    label: 'Leistungserbringer erhalten keine Daten mehr.',
  },
];

const noAcknowledgements: Record<ConsequenceKey, boolean> = {
  manualLinks: false,
  appAccess: false,
  healthcareProviders: false,
};
